import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Clock,
  User,
  Info,
  AlertTriangle,
  CheckCircle,
  FileCheck2,
  Search,
  Trash2,
  Users,
  Lock,
  Mail,
  Edit2,
  Plus,
  X,
  Save,
  UserPlus,
  Key,
  Eye,
  Shield,
  Building,
  MapPin,
  Briefcase,
  Bell,
  BellOff,
  Sliders,
  Bot,
  Cpu,
  Zap,
  Image as ImageIcon,
  Upload,
  Palette,
  Globe,
  Loader2,
  Send,
  Smartphone,
  Inbox,
  RefreshCw,
  Link2,
} from 'lucide-react';

import {
  ActivityLog,
  SystemUser,
  UserRole,
  CompanyProfile,
  AppSettings,
  GovernanceOverview,
} from '../types';
import { getLogs, logActivity } from '../services/auditService';
import { apiService, TenderMailbox, TenderMailboxMessage } from '../services/apiService';
import { settingsService } from '../services/settingsService';
import { AgentSettings, opportunityAgentService } from '../services/opportunityAgentService';
import { ALL_MENU_PERMISSION_KEYS, MENU_PERMISSION_GROUPS, MenuPermissionKey } from '../config/menuPermissions';

import { useModal } from './ModalProvider';

const DEFAULT_SETTINGS: AppSettings = {
  aiProvider: 'GEMINI',
  emailRecipients: 'equipe@empresa.com',
  smtpPort: 587,
  smtpSecure: false,
  smtpFromName: 'Sistema ULTRA-LICITAÇÃO',
  notifyStatusChange: true,
  notifyDeadlineWarning: true,
  notifyNewCommunication: false,
  notifyContractRenewal: true,
  notifyDocumentReadiness: true,
};

const DEFAULT_AGENT_SETTINGS: AgentSettings = {
  provider: 'GEMINI', openaiModel: 'gpt-4o-mini', geminiModel: 'gemini-3.6-flash',
  autoAnalyze: true, minimumScore: 70, dailyLimit: 5, analysisInstructions: '',
  calendarEnabled: true, calendarReminderMinutes: 60,
};

const Settings: React.FC = () => {
  // Controle de Abas
  const [activeTab, setActiveTab] = useState<'GENERAL' | 'SOURCES' | 'AI_AGENT' | 'COMPANIES' | 'USERS' | 'AUDIT' | 'GOVERNANCE'>(
    () => window.location.hash.includes('tab=ai-agent') ? 'AI_AGENT' : window.location.hash.includes('tab=sources') ? 'SOURCES' : 'GENERAL'
  );

  // --- Estados Gerais ---
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [tempOpenAIKey, setTempOpenAIKey] = useState('');
  const [tempGeminiKey, setTempGeminiKey] = useState('');
  const [originalSmtpPassword, setOriginalSmtpPassword] = useState(''); // Rastreia senha original do backend
  const [testingEmail, setTestingEmail] = useState(false);
  const [agentSettings, setAgentSettings] = useState<AgentSettings>(DEFAULT_AGENT_SETTINGS);
  const [personalOpenAIKey, setPersonalOpenAIKey] = useState('');
  const [personalGeminiKey, setPersonalGeminiKey] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);
  const [testingAgent, setTestingAgent] = useState(false);

  // --- Estados de Auditoria ---
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [logSearchTerm, setLogSearchTerm] = useState('');
  const [governance, setGovernance] = useState<GovernanceOverview | null>(null);
  const [governanceLoading, setGovernanceLoading] = useState(false);
  const [governanceSaving, setGovernanceSaving] = useState(false);
  const [accessSecurity, setAccessSecurity] = useState<Awaited<ReturnType<typeof apiService.getAccessSecurityOverview>> | null>(null);
  const [accessSecurityLoading, setAccessSecurityLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [securityActionLoading, setSecurityActionLoading] = useState(false);

  // --- Estados de Usuários ---
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null);

  // --- Estados de Empresas ---
  const [companies, setCompanies] = useState<CompanyProfile[]>([]);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyProfile | null>(null);
  const [companyForm, setCompanyForm] = useState<Partial<CompanyProfile>>({});

  // --- Estados das fontes de e-mail para editais ---
  const [mailboxes, setMailboxes] = useState<TenderMailbox[]>([]);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string | null>(null);
  const [mailboxMessages, setMailboxMessages] = useState<TenderMailboxMessage[]>([]);
  const [availableTenders, setAvailableTenders] = useState<Array<{ id: string; title: string; organ: string }>>([]);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxActionId, setMailboxActionId] = useState<string | null>(null);
  const [editingMailbox, setEditingMailbox] = useState<TenderMailbox | null>(null);
  const [mailboxForm, setMailboxForm] = useState<Partial<TenderMailbox> & { password?: string }>({
    name: '', email: '', host: 'imap.gmail.com', port: 993, secure: true, username: '', password: '', folder: 'INBOX',
    senderFilters: [], subjectKeywords: [], onlyWithAttachments: true, lookbackDays: 14, maxMessagesPerSync: 30, syncIntervalMinutes: 30, active: true,
  });

  // Form Usuário
  const [userForm, setUserForm] = useState<Partial<SystemUser>>({
    name: '',
    email: '',
    role: 'OPERATOR',
    active: true,
    department: '',
    mobileAccessEnabled: true,
    mobileNotificationsEnabled: true,
  });

  // Carregar dados
  useEffect(() => {
    // Carregar Configurações Gerais (do DB)
    const loadSettings = async () => {
      const stored = await settingsService.getAppSettings();
      if (stored) {
        setSettings({
          ...DEFAULT_SETTINGS,
          ...stored,
        });
        // Preencher chaves de API temporárias se estiverem mascaradas
        if (stored.openaiApiKey && stored.openaiApiKey !== '********') {
          setTempOpenAIKey(stored.openaiApiKey);
        }
        if (stored.geminiApiKey && stored.geminiApiKey !== '********') {
          setTempGeminiKey(stored.geminiApiKey);
        }
        // Armazenar senha SMTP original (mascarada ou não)
        if (stored.smtpPassword) {
          setOriginalSmtpPassword(stored.smtpPassword);
        }
      }
    };
    loadSettings();

    const loadAgentSettings = async () => {
      try {
        setAgentSettings({ ...DEFAULT_AGENT_SETTINGS, ...(await opportunityAgentService.getSettings()) });
      } catch (err) {
        console.error('Erro ao carregar configurações pessoais do Analista IA:', err);
      }
    };
    loadAgentSettings();

    // Carregar Logs (agora async)
    const loadLogs = async () => {
      try {
        const data = await getLogs();
        setLogs(data);
      } catch (err) {
        console.error('Erro ao carregar logs:', err);
      }
    };
    loadLogs();
    const interval = setInterval(loadLogs, 5000);

    // Carregar Usuários (do backend)
    const loadUsers = async () => {
      try {
        const list = await apiService.getUsers();
        setUsers(list || []);
      } catch (err) {
        console.error('Erro ao carregar usuários do servidor:', err);
      }
    };
    loadUsers();

    // Carregar Empresas (do backend)
    const loadCompanies = async () => {
      try {
        const list = await apiService.getCompanies();
        setCompanies(list || []);
      } catch (err) {
        console.error('Erro ao carregar empresas do servidor:', err);
      }
    };
    loadCompanies();

    return () => clearInterval(interval);
  }, []);

  const { showAlert, showConfirm } = useModal();

  useEffect(() => {
    if (activeTab !== 'GOVERNANCE') return;
    let cancelled = false;
    setGovernanceLoading(true);
    apiService.getGovernanceOverview()
      .then((data) => { if (!cancelled) setGovernance(data); })
      .catch((error) => { if (!cancelled) void showAlert(error instanceof Error ? error.message : 'Não foi possível carregar a governança.'); })
      .finally(() => { if (!cancelled) setGovernanceLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab, showAlert]);

  useEffect(() => {
    if (activeTab !== 'GOVERNANCE') return;
    let cancelled = false;
    setAccessSecurityLoading(true);
    apiService.getAccessSecurityOverview()
      .then((data) => { if (!cancelled) setAccessSecurity(data); })
      .catch((error) => {
        // Usuários sem papel administrativo não recebem o histórico sensível.
        if (!cancelled) console.warn('Segurança de acesso indisponível:', error);
      })
      .finally(() => { if (!cancelled) setAccessSecurityLoading(false); });
    return () => { cancelled = true; };
  }, [activeTab]);

  const refreshAccessSecurity = async () => {
    setAccessSecurityLoading(true);
    try {
      setAccessSecurity(await apiService.getAccessSecurityOverview());
    } finally {
      setAccessSecurityLoading(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    try {
      setSecurityActionLoading(true);
      const result = await apiService.regenerateRecoveryCodes();
      setRecoveryCodes(result.recoveryCodes || []);
      await refreshAccessSecurity();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível gerar códigos de recuperação.', 'Erro');
    } finally {
      setSecurityActionLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      setSecurityActionLoading(true);
      await apiService.revokeRemoteSession(sessionId);
      await refreshAccessSecurity();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível encerrar a sessão.', 'Erro');
    } finally {
      setSecurityActionLoading(false);
    }
  };

  // --- Lógica Geral ---
  const handleSaveGeneral = async () => {
    try {
      const settingsToSave = { ...settings };

      // Não enviar chaves de API se estiverem mascaradas
      if (settings.aiProvider === 'OPENAI' && tempOpenAIKey && tempOpenAIKey !== '********') {
        settingsToSave.openaiApiKey = tempOpenAIKey;
      } else if (settings.openaiApiKey === '********') {
        delete settingsToSave.openaiApiKey; // Não sobrescrever se estiver mascarada
      }

      if (settings.aiProvider === 'GEMINI' && tempGeminiKey && tempGeminiKey !== '********') {
        settingsToSave.geminiApiKey = tempGeminiKey;
      } else if (settings.geminiApiKey === '********') {
        delete settingsToSave.geminiApiKey; // Não sobrescrever se estiver mascarada
      }

      // Não enviar senha SMTP se não foi alterada (ainda é a original mascarada)
      if (settings.smtpPassword === originalSmtpPassword && originalSmtpPassword === '********') {
        delete settingsToSave.smtpPassword; // Não sobrescrever se não foi alterada
      }
      // Se a senha mudou, enviar a nova senha (mesmo que pareça mascarada no input)

      await settingsService.saveAppSettings(settingsToSave);

      await showAlert('✅ Configurações salvas com sucesso no banco de dados!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      await showAlert('❌ Erro ao salvar configurações.');
    }
  };

  const handleSaveGovernance = async () => {
    if (!governance) return;
    setGovernanceSaving(true);
    try {
      const policy = await apiService.saveGovernancePolicy(governance.policy);
      setGovernance((current) => current ? { ...current, policy } : current);
      await showAlert('Política de governança salva e registrada na auditoria.');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível salvar a política de governança.');
    } finally {
      setGovernanceSaving(false);
    }
  };

  const handleSaveAgent = async () => {
    setSavingAgent(true);
    try {
      await opportunityAgentService.saveSettings({
        ...agentSettings,
        openaiApiKey: personalOpenAIKey || undefined,
        geminiApiKey: personalGeminiKey || undefined,
      });
      setPersonalOpenAIKey('');
      setPersonalGeminiKey('');
      setAgentSettings({ ...DEFAULT_AGENT_SETTINGS, ...(await opportunityAgentService.getSettings()) });
      await showAlert('Configurações pessoais do Analista IA salvas com segurança.');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao salvar o Analista IA.');
    } finally {
      setSavingAgent(false);
    }
  };

  const handleTestAgent = async () => {
    if (personalOpenAIKey || personalGeminiKey) await handleSaveAgent();
    setTestingAgent(true);
    try {
      const result = await opportunityAgentService.testSettings();
      await showAlert(result.success ? `Conexão validada com ${result.provider} (${result.model}).` : 'O provedor respondeu, mas o teste não foi confirmado.');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Falha ao testar o provedor.');
    } finally {
      setTestingAgent(false);
    }
  };

  const handleTestEmail = async () => {
    // Validar configurações SMTP
    if (!settings.smtpHost || !settings.smtpUser || !settings.smtpPassword) {
      await showAlert('Por favor, preencha o servidor SMTP, usuário e senha antes de testar.');
      return;
    }

    if (!settings.emailRecipients) {
      await showAlert(
        'Por favor, defina pelo menos um destinatário no campo acima para receber o teste.'
      );
      return;
    }

    setTestingEmail(true);
    try {
      // Extrair todos os destinatários da lista
      const recipients = settings.emailRecipients
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (recipients.length === 0) {
        await showAlert('Nenhum destinatário válido encontrado.');
        setTestingEmail(false);
        return;
      }

      const result = await settingsService.testEmail(undefined, recipients);

      if (result.success) {
        const recipientList = recipients.join(', ');
        await showAlert(
          `✅ E-mail de teste enviado com sucesso para ${recipients.length} destinatário(s):\n\n${recipientList}`
        );
      } else {
        await showAlert(`❌ Erro ao enviar e-mail: ${result.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error(error);
      await showAlert(
        '❌ Erro ao enviar e-mail de teste. Verifique o console para mais detalhes e confira suas credenciais SMTP.'
      );
    } finally {
      setTestingEmail(false);
    }
  };

  // --- Lógica de Auditoria ---
  const getLogIcon = (type: ActivityLog['type']) => {
    switch (type) {
      case 'SUCCESS':
        return <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" />;
      case 'WARNING':
        return <AlertTriangle className="w-4 h-4 text-orange-500 dark:text-orange-400" />;
      case 'ERROR':
        return <AlertTriangle className="w-4 h-4 text-red-500 dark:text-red-400" />;
      default:
        return <Info className="w-4 h-4 text-blue-500 dark:text-blue-400" />;
    }
  };

  const filteredLogs = logs.filter(
    (log) =>
      log.action.toLowerCase().includes(logSearchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(logSearchTerm.toLowerCase()) ||
      log.user.toLowerCase().includes(logSearchTerm.toLowerCase())
  );

  // --- Lógica de Usuários ---

  const handleOpenUserModal = (user?: SystemUser) => {
    if (user) {
      setEditingUser(user);
      setUserForm(user);
    } else {
      setEditingUser(null);
      setUserForm({
        name: '',
        email: '',
        role: 'OPERATOR',
        active: true,
        department: '',
        menuKeys: [...ALL_MENU_PERMISSION_KEYS],
        mobileAccessEnabled: true,
        mobileNotificationsEnabled: true,
      });
    }
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      await showAlert('Nome e E-mail são obrigatórios.');
      return;
    }

    try {
      if (editingUser) {
        const updated = await apiService.updateUser(editingUser.id, userForm);
        setUsers(users.map((u) => (u.id === editingUser.id ? updated : u)));
        logActivity('USUARIO_EDICAO', `Usuário atualizado: ${userForm.email}`, 'INFO');
      } else {
        const created = await apiService.createUser(userForm);
        setUsers([...users, created]);
        logActivity(
          'USUARIO_CRIACAO',
          `Novo usuário criado: ${userForm.email} (${userForm.role})`,
          'SUCCESS'
        );
      }
      setIsUserModalOpen(false);
    } catch (error: unknown) {
      const msg = (error as Error)?.message || 'Erro ao salvar usuário';
      await showAlert(msg);
    }
  };

  const handleDeleteUser = async (id: string, name: string) => {
    const ok = await showConfirm(`Tem certeza que deseja remover o acesso de ${name}?`);
    if (!ok) return;
    try {
      await apiService.deleteUser(id);
      setUsers(users.filter((u) => u.id !== id));
      logActivity('USUARIO_EXCLUSAO', `Usuário removido: ${name}`, 'WARNING');
    } catch (error: unknown) {
      const msg = (error as Error)?.message || 'Erro ao excluir usuário';
      await showAlert(msg);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const updated = await apiService.updateUser(id, { active: !currentStatus });
      setUsers(users.map((u) => (u.id === id ? updated : u)));
      const action = !currentStatus ? 'ativado' : 'desativado';
      logActivity('USUARIO_STATUS', `Usuário ID ${id} foi ${action}`, 'INFO');
    } catch (error: unknown) {
      const msg = (error as Error)?.message || 'Erro ao alterar status';
      await showAlert(msg);
    }
  };

  const selectedMenuKeys = new Set<MenuPermissionKey>(
    (userForm.menuKeys || ALL_MENU_PERMISSION_KEYS) as MenuPermissionKey[],
  );
  const toggleMenuKey = (key: MenuPermissionKey) => {
    const next = new Set(selectedMenuKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setUserForm({ ...userForm, menuKeys: [...next] });
  };
  const toggleMenuGroup = (keys: MenuPermissionKey[]) => {
    const next = new Set(selectedMenuKeys);
    const allSelected = keys.every((key) => next.has(key));
    keys.forEach((key) => allSelected ? next.delete(key) : next.add(key));
    setUserForm({ ...userForm, menuKeys: [...next] });
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.department?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  const handleOpenCompanyModal = (company?: CompanyProfile) => {
    if (company) {
      setEditingCompany(company);
      setCompanyForm(company);
    } else {
      setEditingCompany(null);
      setCompanyForm({
        isDefault: companies.length === 0, // Se for a primeira, já sugere como padrão
        corporateName: '',
        tradeName: '',
        cnpj: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        email: '',
        phone: '',
        website: '',
        representativeName: '',
        representativeRole: '',
        representativeCpf: '',
        logo: '',
        primaryColor: '#2563eb', // Default Blue
        secondaryColor: '#f1f5f9', // Default Slate
      });
    }
    setIsCompanyModalOpen(true);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 500 * 1024) {
        // 500KB limit
        showAlert('A imagem deve ter no máximo 500KB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanyForm((prev) => ({ ...prev, logo: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCompany = async () => {
    if (!companyForm.corporateName || !companyForm.cnpj) {
      await showAlert('Razão Social e CNPJ são obrigatórios.');
      return;
    }

    try {
      if (editingCompany) {
        // Update existing
        const payload: Partial<CompanyProfile> = { ...companyForm };
        const updated = await apiService.updateCompany(editingCompany.id, payload);
        logActivity('EMPRESA_EDICAO', `Empresa atualizada: ${updated.corporateName}`, 'INFO');
      } else {
        // Create new
        const created = await apiService.createCompany(companyForm as Partial<CompanyProfile>);
        logActivity(
          'EMPRESA_CRIACAO',
          `Nova empresa cadastrada: ${created.corporateName}`,
          'SUCCESS'
        );
      }

      // Refresh list from server
      const fresh = await apiService.getCompanies();
      if (fresh) setCompanies(fresh);

      // Notify other parts of the app that companies changed
      try {
        window.dispatchEvent(new Event('companies:changed'));
      } catch (e) {
        console.warn('dispatch companies:changed failed', e);
      }
      setIsCompanyModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar empresa', err);
      await showAlert('Falha ao salvar empresa. Veja o console para mais detalhes.');
    }
  };

  const handleDeleteCompany = async (id: string, name: string) => {
    if (companies.length <= 1) {
      await showAlert('Você deve manter pelo menos uma empresa cadastrada.');
      return;
    }
    const ok = await showConfirm(`Excluir empresa ${name}?`);
    if (!ok) return;

    try {
      await apiService.deleteCompany(id);
      logActivity('EMPRESA_EXCLUSAO', `Empresa excluída: ${name}`, 'WARNING');

      const fresh = await apiService.getCompanies();
      if (fresh) setCompanies(fresh);

      // Notify other parts of the app that companies changed
      try {
        window.dispatchEvent(new Event('companies:changed'));
      } catch (e) {}
    } catch (err: any) {
      console.error('Erro ao deletar empresa', err);

      // Se houver detalhes das dependências, montar mensagem detalhada
      let message = err.message || 'Falha ao deletar empresa.';

      if (err.details) {
        const details = err.details;
        const items: string[] = [];

        if (details.contratos?.length) {
          items.push(`\n\n📄 Contratos (${details.contratos.length}):\n` +
            details.contratos.map((c: any) => `  • ${c.titulo} (ID: ${c.id})`).join('\n'));
        }
        if (details.documentos?.length) {
          items.push(`\n\n📋 Documentos (${details.documentos.length}):\n` +
            details.documentos.map((d: any) => `  • ${d.titulo} (ID: ${d.id})`).join('\n'));
        }
        if (details.ordens?.length) {
          items.push(`\n\n🛒 Ordens de Compra (${details.ordens.length}):\n` +
            details.ordens.map((o: any) => `  • ${o.titulo} (ID: ${o.id})`).join('\n'));
        }
        if (details.comunicacoes?.length) {
          items.push(`\n\n💬 Comunicações (${details.comunicacoes.length}):\n` +
            details.comunicacoes.map((c: any) => `  • ${c.assunto} (ID: ${c.id})`).join('\n'));
        }
        if (details.portais?.length) {
          items.push(`\n\n🌐 Portais (${details.portais.length}):\n` +
            details.portais.map((p: any) => `  • ${p.nome} (ID: ${p.id})`).join('\n'));
        }
        if (details.templates?.length) {
          items.push(`\n\n📝 Templates (${details.templates.length}):\n` +
            details.templates.map((t: any) => `  • ${t.nome} (ID: ${t.id})`).join('\n'));
        }

        if (items.length > 0) {
          message += '\n\n⚠️ Registros vinculados que impedem a exclusão:' + items.join('');
        }
      }

      await showAlert(message);
    }
  };

  const resetMailboxForm = () => {
    setEditingMailbox(null);
    setMailboxForm({
      name: '', email: '', host: 'imap.gmail.com', port: 993, secure: true, username: '', password: '', folder: 'INBOX',
      senderFilters: [], subjectKeywords: [], onlyWithAttachments: true, lookbackDays: 14, maxMessagesPerSync: 30, syncIntervalMinutes: 30, active: true,
    });
  };

  const loadMailboxMessages = async (mailboxId: string) => {
    setSelectedMailboxId(mailboxId);
    try {
      const [messages, tenders] = await Promise.all([apiService.getTenderMailboxMessages(mailboxId), apiService.getTenders()]);
      setMailboxMessages(messages);
      setAvailableTenders(tenders.map((tender) => ({ id: tender.id, title: tender.title, organ: tender.organ })));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível carregar as mensagens importadas.');
    }
  };

  const loadMailboxes = async () => {
    setMailboxLoading(true);
    try {
      setMailboxes(await apiService.getTenderMailboxes());
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível carregar as fontes de e-mail.');
    } finally {
      setMailboxLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'SOURCES') void loadMailboxes();
  }, [activeTab]);

  const handleSaveMailbox = async () => {
    try {
      setMailboxLoading(true);
      await apiService.saveTenderMailbox(mailboxForm, editingMailbox?.id);
      await loadMailboxes();
      resetMailboxForm();
      await showAlert('Fonte de e-mail salva. Use Testar conexão antes da primeira importação.');
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível salvar a fonte de e-mail.');
    } finally {
      setMailboxLoading(false);
    }
  };

  const handleMailboxAction = async (mailbox: TenderMailbox, action: 'TEST' | 'SYNC') => {
    setMailboxActionId(mailbox.id);
    try {
      if (action === 'TEST') {
        const result = await apiService.testTenderMailbox(mailbox.id);
        await showAlert(result.message);
      } else {
        const result = await apiService.syncTenderMailbox(mailbox.id);
        await showAlert(`${result.imported} mensagem(ns) importada(s), ${result.attachments} anexo(s) salvo(s), ${result.linked} vínculo(s) automático(s) e ${result.pending} pendência(s) para revisão.`);
        await loadMailboxMessages(mailbox.id);
      }
      await loadMailboxes();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível concluir a ação na caixa de e-mail.');
    } finally {
      setMailboxActionId(null);
    }
  };

  const handleLinkMailboxMessage = async (messageId: string, tenderId: string) => {
    if (!tenderId) return;
    try {
      setMailboxActionId(messageId);
      const result = await apiService.linkTenderMailboxMessage(messageId, tenderId);
      await showAlert(`${result.attached} anexo(s) foram vinculados ao dossiê da licitação.`);
      if (selectedMailboxId) await loadMailboxMessages(selectedMailboxId);
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível vincular a mensagem.');
    } finally {
      setMailboxActionId(null);
    }
  };

  const handleDeleteMailbox = async (mailbox: TenderMailbox) => {
    const confirmed = await showConfirm(`Remover a fonte ${mailbox.email}? Os anexos já vinculados às licitações serão preservados.`, 'Remover fonte de e-mail');
    if (!confirmed) return;
    try {
      setMailboxActionId(mailbox.id);
      await apiService.deleteTenderMailbox(mailbox.id);
      if (selectedMailboxId === mailbox.id) {
        setSelectedMailboxId(null);
        setMailboxMessages([]);
      }
      await loadMailboxes();
      resetMailboxForm();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível remover a fonte de e-mail.');
    } finally {
      setMailboxActionId(null);
    }
  };

  const getRoleBadge = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200">
            <Shield className="w-3 h-3" /> Admin
          </span>
        );
      case 'MANAGER':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60">
            <Key className="w-3 h-3" /> Gestor
          </span>
        );
      case 'OPERATOR':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800/60">
            <User className="w-3 h-3" /> Operador
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark border border-line dark:border-line-dark">
            <Eye className="w-3 h-3" /> Visualizador
          </span>
        );
    }
  };

  const getRoleDescription = (role: UserRole) => {
    switch (role) {
      case 'ADMIN':
        return 'Acesso total ao sistema, configurações e auditoria.';
      case 'MANAGER':
        return 'Gerencia licitações, contratos e equipe. Não acessa auditoria.';
      case 'OPERATOR':
        return 'Pode criar e editar licitações e contratos.';
      case 'VIEWER':
        return 'Apenas visualização. Não pode alterar dados.';
    }
  };

  return (
    <div className="space-y-6">
      {/* ... (Header Tabs mantidas) ... */}
      <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-sm border border-line dark:border-line-dark overflow-hidden">
        <div className="p-6 pb-0 border-b border-line dark:border-line-dark">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl font-bold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                Configurações do Sistema
              </h2>
              <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                Gerencie preferências gerais, empresas, usuários e visualize logs.
              </p>
            </div>
          </div>

          <div className="flex gap-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab('GENERAL')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'GENERAL'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark'
              }`}
            >
              <Sliders className="w-4 h-4" />
              Geral & Notificações
            </button>
            <button
              onClick={() => setActiveTab('AI_AGENT')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'AI_AGENT'
                  ? 'border-brand-500 text-brand-600 dark:text-brand-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:hover:text-ink-text-dark'
              }`}
            >
              <Bot className="w-4 h-4" />
              Analista IA
            </button>
            <button
              onClick={() => setActiveTab('SOURCES')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'SOURCES'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:hover:text-ink-text-dark'
              }`}
            >
              <Inbox className="w-4 h-4" />
              Fontes e e-mail
            </button>
            <button
              onClick={() => setActiveTab('COMPANIES')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'COMPANIES'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark'
              }`}
            >
              <Building className="w-4 h-4" />
              Minhas Empresas
            </button>
            <button
              onClick={() => setActiveTab('USERS')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'USERS'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark'
              }`}
            >
              <Users className="w-4 h-4" />
              Usuários e Permissões
            </button>
            <button
              onClick={() => setActiveTab('AUDIT')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'AUDIT'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark'
              }`}
            >
              <ShieldCheck className="w-4 h-4" />
              Auditoria & Logs
            </button>
            <button
              onClick={() => setActiveTab('GOVERNANCE')}
              className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'GOVERNANCE'
                  ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-ink-subtitle dark:text-ink-subtitle-dark hover:text-ink-text dark:text-ink-text-dark'
              }`}
            >
              <Shield className="w-4 h-4" />
              Governança e Segurança
            </button>
          </div>
        </div>

        {/* ... (Aba GERAL mantida) ... */}
        {activeTab === 'GENERAL' && (
          <div className="animate-fade-in p-6">
            <div className="max-w-4xl mx-auto space-y-8">
              {/* Configuração de IA da organização mantida apenas para compatibilidade legada. */}
              <div className="hidden" aria-hidden="true">
                <h3 className="text-lg font-bold text-ink-title dark:text-ink-title-dark mb-1 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-600 dark:text-purple-300" />
                  Motor de Inteligência Artificial
                </h3>
                <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mb-4">
                  Escolha qual cérebro de IA processará seus documentos e editais.
                </p>

                <div className="bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark p-6 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label
                      className={`relative flex items-start p-4 cursor-pointer rounded-lg border-2 transition-all ${
                        settings.aiProvider === 'GEMINI'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-line dark:border-line-dark hover:border-line-strong dark:border-line-dark-strong'
                      }`}
                    >
                      <input
                        type="radio"
                        name="aiProvider"
                        value="GEMINI"
                        checked={settings.aiProvider === 'GEMINI'}
                        onChange={() => setSettings({ ...settings, aiProvider: 'GEMINI' })}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded-lg">
                          <Cpu className="w-6 h-6" />
                        </div>
                        <div>
                          <span className="block font-bold text-ink-title dark:text-ink-title-dark">
                            Google Gemini (Padrão)
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                            Rápido, gratuito (Flash 2.5) e já integrado. Ideal para maioria dos
                            casos.
                          </span>
                        </div>
                        {settings.aiProvider === 'GEMINI' && (
                          <CheckCircle className="absolute top-4 right-4 w-5 h-5 text-blue-600 dark:text-blue-300" />
                        )}
                      </div>
                    </label>

                    <label
                      className={`relative flex items-start p-4 cursor-pointer rounded-lg border-2 transition-all ${
                        settings.aiProvider === 'OPENAI'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                          : 'border-line dark:border-line-dark hover:border-line-strong dark:border-line-dark-strong'
                      }`}
                    >
                      <input
                        type="radio"
                        name="aiProvider"
                        value="OPENAI"
                        checked={settings.aiProvider === 'OPENAI'}
                        onChange={() => setSettings({ ...settings, aiProvider: 'OPENAI' })}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-300 rounded-lg">
                          <Bot className="w-6 h-6" />
                        </div>
                        <div>
                          <span className="block font-bold text-ink-title dark:text-ink-title-dark">OpenAI (ChatGPT)</span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                            Usa o modelo GPT-4o. Requer sua própria chave de API (paga).
                          </span>
                        </div>
                        {settings.aiProvider === 'OPENAI' && (
                          <CheckCircle className="absolute top-4 right-4 w-5 h-5 text-green-600 dark:text-green-300" />
                        )}
                      </div>
                    </label>
                  </div>

                  {settings.aiProvider === 'OPENAI' && (
                    <div className="mt-4 pt-4 border-t border-line dark:border-line-dark animate-fade-in-down">
                      <label
                        htmlFor="openai-key"
                        className="block text-xs font-bold text-ink-text dark:text-ink-text-dark mb-1"
                      >
                        Sua Chave de API da OpenAI (sk-...)
                      </label>
                      <input
                        id="openai-key"
                        type="password"
                        value={tempOpenAIKey}
                        onChange={(e) => setTempOpenAIKey(e.target.value)}
                        placeholder="sk-proj-..."
                        className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 outline-none font-mono"
                      />
                      <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">
                        A chave é salva localmente no seu navegador e nunca enviada para nossos
                        servidores.
                      </p>
                    </div>
                  )}
                  {settings.aiProvider === 'GEMINI' && (
                    <div className="mt-4 pt-4 border-t border-line dark:border-line-dark animate-fade-in-down">
                      <label
                        htmlFor="gemini-key"
                        className="block text-xs font-bold text-ink-text dark:text-ink-text-dark mb-1"
                      >
                        Sua Chave de API do Gemini (opcional)
                      </label>
                      <input
                        id="gemini-key"
                        type="password"
                        value={tempGeminiKey}
                        onChange={(e) => setTempGeminiKey(e.target.value)}
                        placeholder="AIza... ou chave de projeto"
                        className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none font-mono"
                      />
                      <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">
                        A chave será salva no banco de dados associada ao usuário de sistema.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Seção Notificações */}
              <div>
                <h3 className="text-lg font-bold text-ink-title dark:text-ink-title-dark mb-1 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                  Notificações & Alertas
                </h3>
                <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mb-4">
                  Configure quando e como a equipe deve ser avisada.
                </p>

                <div className="bg-surface dark:bg-surface-dark rounded-xl border border-line dark:border-line-dark p-6 shadow-sm space-y-6">
                  {/* E-mails */}
                  <div>
                    <label
                      htmlFor="email-recipients"
                      className="block text-xs font-bold text-ink-text dark:text-ink-text-dark mb-1"
                    >
                      Destinatários (separados por vírgula)
                    </label>
                    <input
                      id="email-recipients"
                      type="text"
                      value={settings.emailRecipients}
                      onChange={(e) =>
                        setSettings({ ...settings, emailRecipients: e.target.value })
                      }
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      placeholder="ex: equipe@licitaflow.com, gestor@licitaflow.com"
                    />
                  </div>

                  {/* CONFIGURAÇÃO SMTP */}
                  <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-sm font-bold text-blue-800 dark:text-blue-200 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Configuração do Servidor SMTP
                      </h4>
                      <button
                        onClick={handleTestEmail}
                        disabled={testingEmail}
                        className="text-xs bg-brand-500 text-white px-3 py-1 rounded hover:bg-brand-600 disabled:opacity-50 flex items-center gap-1 transition-colors shadow-sm"
                      >
                        {testingEmail ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        Testar Envio
                      </button>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-300 mb-4">
                      Configure seu próprio servidor SMTP para enviar e-mails diretamente. Suporta Gmail, Outlook, Amazon SES, etc.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label
                          htmlFor="smtp-host"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          Servidor SMTP (Host) *
                        </label>
                        <input
                          id="smtp-host"
                          type="text"
                          value={settings.smtpHost || ''}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpHost: e.target.value,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="smtp.gmail.com"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="smtp-port"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          Porta SMTP
                        </label>
                        <input
                          id="smtp-port"
                          type="number"
                          value={settings.smtpPort || 587}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpPort: parseInt(e.target.value) || 587,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="587"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="smtp-user"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          Usuário SMTP *
                        </label>
                        <input
                          id="smtp-user"
                          type="text"
                          value={settings.smtpUser || ''}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpUser: e.target.value,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="seu-email@gmail.com"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="smtp-password"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          Senha / App Password *
                        </label>
                        <input
                          id="smtp-password"
                          type="password"
                          value={settings.smtpPassword || ''}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpPassword: e.target.value,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="******"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="smtp-from-name"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          Nome do Remetente
                        </label>
                        <input
                          id="smtp-from-name"
                          type="text"
                          value={settings.smtpFromName || ''}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpFromName: e.target.value,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="Sistema ULTRA-LICITAÇÃO"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="smtp-from-email"
                          className="block text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark mb-1"
                        >
                          E-mail do Remetente
                        </label>
                        <input
                          id="smtp-from-email"
                          type="email"
                          value={settings.smtpFromEmail || ''}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              smtpFromEmail: e.target.value,
                            })
                          }
                          className="w-full border border-blue-200 dark:border-blue-800/60 rounded px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-brand-500"
                          placeholder="noreply@empresa.com"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-200 dark:border-blue-800/60">
                      <input
                        type="checkbox"
                        id="smtp-secure"
                        checked={settings.smtpSecure || false}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            smtpSecure: e.target.checked,
                          })
                        }
                        className="rounded text-blue-600 dark:text-blue-300 focus:ring-brand-500"
                      />
                      <label htmlFor="smtp-secure" className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                        Usar SSL/TLS (porta 465) - Desmarque para STARTTLS (porta 587)
                      </label>
                    </div>

                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800/60">
                      <p className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark">
                        💡 <strong>Dica Gmail:</strong> Use uma "Senha de App" ao invés da sua senha normal.
                        Acesse: Conta Google → Segurança → Verificação em 2 etapas → Senhas de app
                      </p>
                    </div>
                  </div>

                  {/* Tipos de Alerta (Checkboxes) */}
                  <div>
                    <span className="block text-xs font-bold text-ink-text dark:text-ink-text-dark mb-3 uppercase tracking-wide">
                      Tipos de Alerta por E-mail
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center gap-3 p-3 border border-line dark:border-line-dark rounded-lg cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt">
                        <input
                          type="checkbox"
                          aria-label="Mudança de Status Crítico"
                          checked={settings.notifyStatusChange ?? true}
                          onChange={(e) =>
                            setSettings({ ...settings, notifyStatusChange: e.target.checked })
                          }
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            Mudança de Status Crítico
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            Quando licitação virar &quot;Ganha&quot; ou &quot;Alta Prioridade&quot;
                          </span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-line dark:border-line-dark rounded-lg cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt">
                        <input
                          type="checkbox"
                          aria-label="Pendências documentais"
                          checked={settings.notifyDocumentReadiness ?? true}
                          onChange={(e) =>
                            setSettings({ ...settings, notifyDocumentReadiness: e.target.checked })
                          }
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            Pendências documentais
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            Registrar alertas quando o edital tiver requisitos sem documento válido
                          </span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-line dark:border-line-dark rounded-lg cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt">
                        <input
                          type="checkbox"
                          aria-label="Aviso de Prazos Próximos"
                          checked={settings.notifyDeadlineWarning ?? true}
                          onChange={(e) =>
                            setSettings({ ...settings, notifyDeadlineWarning: e.target.checked })
                          }
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            Aviso de Prazos Próximos
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            Alertas de 24h e 3 dias antes do deadline
                          </span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-line dark:border-line-dark rounded-lg cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt">
                        <input
                          type="checkbox"
                          aria-label="Novos Ofícios/Notificações"
                          checked={settings.notifyNewCommunication ?? false}
                          onChange={(e) =>
                            setSettings({ ...settings, notifyNewCommunication: e.target.checked })
                          }
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            Novos Ofícios/Notificações
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            Ao registrar um novo documento recebido
                          </span>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 p-3 border border-line dark:border-line-dark rounded-lg cursor-pointer hover:bg-surface-alt dark:bg-surface-dark-alt">
                        <input
                          type="checkbox"
                          aria-label="Renovação Contratual"
                          checked={settings.notifyContractRenewal ?? true}
                          onChange={(e) =>
                            setSettings({ ...settings, notifyContractRenewal: e.target.checked })
                          }
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 rounded focus:ring-brand-500"
                        />
                        <div>
                          <span className="block text-sm font-medium text-ink-title dark:text-ink-title-dark">
                            Renovação Contratual
                          </span>
                          <span className="block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            Aviso de vencimento de contratos (30 dias)
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-line dark:border-line-dark">
                <button
                  onClick={handleSaveGeneral}
                  className="px-6 py-2.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium shadow-md flex items-center gap-2 transform active:scale-95 transition-all"
                >
                  <Save className="w-4 h-4" />
                  Salvar Todas as Preferências
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'SOURCES' && (
          <div className="animate-fade-in p-6">
            <div className="max-w-6xl mx-auto space-y-7">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div><h3 className="text-lg font-semibold flex items-center gap-2 text-ink-title dark:text-ink-title-dark"><Inbox className="w-5 h-5 text-brand-500" /> Caixa de e-mail de editais</h3><p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Conecte uma caixa IMAP dedicada. Senhas de aplicativo são armazenadas criptografadas.</p></div>
                <button onClick={resetMailboxForm} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"><Plus className="h-4 w-4" />Nova caixa</button>
              </div>

              <section className="border-y border-line dark:border-line-dark py-5">
                <div className="mb-4 flex items-center justify-between"><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Fontes configuradas</h4><button onClick={() => void loadMailboxes()} disabled={mailboxLoading} title="Atualizar fontes" className="rounded p-2 text-ink-subtitle hover:bg-surface-alt dark:hover:bg-surface-dark-alt"><RefreshCw className={`h-4 w-4 ${mailboxLoading ? 'animate-spin' : ''}`} /></button></div>
                <div className="grid gap-3 lg:grid-cols-2">
                  {mailboxes.map((mailbox) => <article key={mailbox.id} className={`border p-4 ${selectedMailboxId === mailbox.id ? 'border-brand-500 bg-brand-50/30 dark:bg-brand-950/10' : 'border-line dark:border-line-dark'}`}>
                    <div className="flex gap-3 justify-between"><div className="min-w-0"><p className="font-medium text-ink-title dark:text-ink-title-dark truncate">{mailbox.name}</p><p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark truncate">{mailbox.email} · {mailbox.host}:{mailbox.port}</p></div><span className={`shrink-0 text-xs font-medium ${mailbox.active ? 'text-emerald-700 dark:text-emerald-300' : 'text-ink-subtitle dark:text-ink-subtitle-dark'}`}>{mailbox.active ? 'Ativa' : 'Pausada'}</span></div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark"><span>{mailbox._count?.messages || 0} mensagens</span><span>·</span><span>{mailbox.syncIntervalMinutes} min</span><span>·</span><span>{mailbox.lastSyncStatus || 'Ainda não sincronizada'}</span></div>
                    {mailbox.lastSyncMessage && <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark line-clamp-2">{mailbox.lastSyncMessage}</p>}
                    <div className="mt-4 flex flex-wrap gap-2"><button onClick={() => void loadMailboxMessages(mailbox.id)} className="rounded border border-line px-3 py-1.5 text-xs font-medium text-ink-text hover:bg-surface-alt dark:border-line-dark dark:text-ink-text-dark dark:hover:bg-surface-dark-alt">Mensagens</button><button onClick={() => void handleMailboxAction(mailbox, 'TEST')} disabled={mailboxActionId === mailbox.id} className="rounded border border-line px-3 py-1.5 text-xs font-medium text-ink-text hover:bg-surface-alt disabled:opacity-50 dark:border-line-dark dark:text-ink-text-dark dark:hover:bg-surface-dark-alt">Testar</button><button onClick={() => void handleMailboxAction(mailbox, 'SYNC')} disabled={mailboxActionId === mailbox.id} className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50">{mailboxActionId === mailbox.id ? 'Lendo...' : 'Importar agora'}</button><button onClick={() => { setEditingMailbox(mailbox); setMailboxForm({ ...mailbox, username: mailbox.username || mailbox.email, password: '' }); }} className="rounded p-1.5 text-ink-subtitle hover:bg-surface-alt dark:hover:bg-surface-dark-alt" title="Editar"><Edit2 className="h-4 w-4" /></button><button onClick={() => void handleDeleteMailbox(mailbox)} className="rounded p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20" title="Remover"><Trash2 className="h-4 w-4" /></button></div>
                  </article>)}
                  {!mailboxes.length && !mailboxLoading && <p className="py-8 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma caixa configurada.</p>}
                </div>
              </section>

              <section className="border-b border-line dark:border-line-dark pb-6">
                <div className="mb-4"><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">{editingMailbox ? `Editar ${editingMailbox.name}` : 'Nova caixa IMAP'}</h4><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Gmail: use senha de aplicativo e `imap.gmail.com`. Microsoft 365: `outlook.office365.com`.</p></div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Nome<input value={mailboxForm.name || ''} onChange={(e) => setMailboxForm({ ...mailboxForm, name: e.target.value })} className="mt-1 input w-full" placeholder="Editais recebidos" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">E-mail<input type="email" value={mailboxForm.email || ''} onChange={(e) => setMailboxForm({ ...mailboxForm, email: e.target.value })} className="mt-1 input w-full" placeholder="editais@empresa.com.br" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Usuário IMAP<input value={mailboxForm.username || ''} onChange={(e) => setMailboxForm({ ...mailboxForm, username: e.target.value })} className="mt-1 input w-full" placeholder="Normalmente o próprio e-mail" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Servidor IMAP<input value={mailboxForm.host || ''} onChange={(e) => setMailboxForm({ ...mailboxForm, host: e.target.value })} className="mt-1 input w-full" placeholder="imap.gmail.com" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Porta<input type="number" min="1" max="65535" value={mailboxForm.port || 993} onChange={(e) => setMailboxForm({ ...mailboxForm, port: Number(e.target.value) })} className="mt-1 input w-full" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Pasta<input value={mailboxForm.folder || 'INBOX'} onChange={(e) => setMailboxForm({ ...mailboxForm, folder: e.target.value })} className="mt-1 input w-full" placeholder="INBOX" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Senha de aplicativo<input type="password" value={mailboxForm.password || ''} onChange={(e) => setMailboxForm({ ...mailboxForm, password: e.target.value })} className="mt-1 input w-full" placeholder={editingMailbox?.credentialConfigured ? 'Mantenha vazio para preservar' : 'Senha de aplicativo'} /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Remetentes permitidos<input value={(mailboxForm.senderFilters || []).join(', ')} onChange={(e) => setMailboxForm({ ...mailboxForm, senderFilters: e.target.value.split(',').map((value) => value.trim()).filter(Boolean) })} className="mt-1 input w-full" placeholder="pncp.gov.br, portal.com.br" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Termos no assunto/corpo<input value={(mailboxForm.subjectKeywords || []).join(', ')} onChange={(e) => setMailboxForm({ ...mailboxForm, subjectKeywords: e.target.value.split(',').map((value) => value.trim()).filter(Boolean) })} className="mt-1 input w-full" placeholder="edital, pregão, licitação" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Intervalo (min)<input type="number" min="5" max="1440" value={mailboxForm.syncIntervalMinutes || 30} onChange={(e) => setMailboxForm({ ...mailboxForm, syncIntervalMinutes: Number(e.target.value) })} className="mt-1 input w-full" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Dias retroativos<input type="number" min="1" max="90" value={mailboxForm.lookbackDays || 14} onChange={(e) => setMailboxForm({ ...mailboxForm, lookbackDays: Number(e.target.value) })} className="mt-1 input w-full" /></label>
                  <label className="text-sm text-ink-text dark:text-ink-text-dark">Máx. mensagens por leitura<input type="number" min="1" max="100" value={mailboxForm.maxMessagesPerSync || 30} onChange={(e) => setMailboxForm({ ...mailboxForm, maxMessagesPerSync: Number(e.target.value) })} className="mt-1 input w-full" /></label>
                </div>
                <div className="mt-4 flex flex-wrap gap-5"><label className="flex items-center gap-2 text-sm text-ink-text dark:text-ink-text-dark"><input type="checkbox" checked={mailboxForm.secure !== false} onChange={(e) => setMailboxForm({ ...mailboxForm, secure: e.target.checked })} />Usar conexão segura (TLS)</label><label className="flex items-center gap-2 text-sm text-ink-text dark:text-ink-text-dark"><input type="checkbox" checked={mailboxForm.onlyWithAttachments !== false} onChange={(e) => setMailboxForm({ ...mailboxForm, onlyWithAttachments: e.target.checked })} />Importar apenas e-mails com anexos</label><label className="flex items-center gap-2 text-sm text-ink-text dark:text-ink-text-dark"><input type="checkbox" checked={mailboxForm.active !== false} onChange={(e) => setMailboxForm({ ...mailboxForm, active: e.target.checked })} />Fonte ativa</label></div>
                <div className="mt-5 flex gap-3"><button onClick={() => void handleSaveMailbox()} disabled={mailboxLoading} className="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"><Save className="h-4 w-4" />Salvar fonte</button>{editingMailbox && <button onClick={resetMailboxForm} className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink-text hover:bg-surface-alt dark:border-line-dark dark:text-ink-text-dark dark:hover:bg-surface-dark-alt">Cancelar</button>}</div>
              </section>

              {selectedMailboxId && <section><div className="mb-4 flex items-center justify-between"><div><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Mensagens importadas</h4><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Vínculos automáticos vão direto ao dossiê. Escolha a licitação para as pendências.</p></div><button onClick={() => void loadMailboxMessages(selectedMailboxId)} className="rounded p-2 text-ink-subtitle hover:bg-surface-alt dark:hover:bg-surface-dark-alt" title="Atualizar mensagens"><RefreshCw className="h-4 w-4" /></button></div><div className="overflow-x-auto border border-line dark:border-line-dark"><table className="w-full text-left text-sm"><thead className="bg-surface-alt text-xs text-ink-subtitle dark:bg-surface-dark-alt dark:text-ink-subtitle-dark"><tr><th className="px-4 py-3">Mensagem</th><th className="px-4 py-3">Anexos</th><th className="px-4 py-3">Situação</th><th className="px-4 py-3">Vincular</th></tr></thead><tbody className="divide-y divide-line dark:divide-line-dark">{mailboxMessages.map((message) => <tr key={message.id}><td className="px-4 py-3"><p className="font-medium text-ink-title dark:text-ink-title-dark">{message.subject}</p><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{message.sender || '-'} {message.receivedAt ? `· ${new Date(message.receivedAt).toLocaleString('pt-BR')}` : ''}</p></td><td className="px-4 py-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{message.attachments?.length || 0}</td><td className="px-4 py-3"><span className={`text-xs font-medium ${message.status === 'LINKED' ? 'text-emerald-700 dark:text-emerald-300' : message.status === 'IGNORED' ? 'text-ink-subtitle dark:text-ink-subtitle-dark' : 'text-amber-700 dark:text-amber-300'}`}>{message.status === 'LINKED' ? `Vinculada${message.tender ? `: ${message.tender.title}` : ''}` : message.status === 'IGNORED' ? 'Ignorada por filtro' : 'Aguardando vínculo'}</span></td><td className="px-4 py-3">{message.status === 'PENDING_LINK' ? <select defaultValue="" onChange={(event) => { if (event.target.value) void handleLinkMailboxMessage(message.id, event.target.value); }} disabled={mailboxActionId === message.id} className="max-w-64 input h-8 text-xs"><option value="">Selecionar licitação</option>{availableTenders.map((tender) => <option key={tender.id} value={tender.id}>{tender.title} · {tender.organ}</option>)}</select> : <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300"><Link2 className="h-3.5 w-3.5" />No dossiê</span>}</td></tr>)}{!mailboxMessages.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma mensagem importada ainda.</td></tr>}</tbody></table></div></section>}
            </div>
          </div>
        )}

        {activeTab === 'AI_AGENT' && (
          <div className="animate-fade-in p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2"><Bot className="w-5 h-5 text-brand-500" /> Meu Analista IA</h3>
                <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark mt-1">Configuração individual. Cada usuário utiliza sua própria conta, chave, limites e instruções.</p>
              </div>

              <section className="border-y border-line dark:border-line-dark py-5 space-y-5">
                <div>
                  <span className="block text-xs font-semibold mb-2">Provedor ativo</span>
                  <div className="inline-flex p-1 rounded-md bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark">
                    {(['GEMINI', 'OPENAI'] as const).map((provider) => (
                      <button key={provider} type="button" onClick={() => setAgentSettings({ ...agentSettings, provider })} className={`h-8 px-4 rounded text-sm font-medium ${agentSettings.provider === provider ? 'bg-surface dark:bg-surface-dark shadow-sm text-brand-600 dark:text-brand-300' : 'text-ink-subtitle'}`}>{provider === 'OPENAI' ? 'OpenAI / GPT' : 'Google Gemini'}</button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="agent-openai-key" className="block text-xs font-semibold mb-1">Chave OpenAI</label>
                    <input id="agent-openai-key" type="password" value={personalOpenAIKey} onChange={(event) => setPersonalOpenAIKey(event.target.value)} placeholder={agentSettings.openaiConfigured ? `Configurada · final ${agentSettings.openaiKeySuffix}` : 'sk-proj-...'} className="w-full h-10 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark font-mono text-sm outline-none focus:ring-2 focus:ring-brand-500/30" />
                    <input value={agentSettings.openaiModel} onChange={(event) => setAgentSettings({ ...agentSettings, openaiModel: event.target.value })} aria-label="Modelo OpenAI" className="w-full h-9 mt-2 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm" />
                  </div>
                  <div>
                    <label htmlFor="agent-gemini-key" className="block text-xs font-semibold mb-1">Chave Gemini</label>
                    <input id="agent-gemini-key" type="password" value={personalGeminiKey} onChange={(event) => setPersonalGeminiKey(event.target.value)} placeholder={agentSettings.geminiConfigured ? `Configurada · final ${agentSettings.geminiKeySuffix}` : 'AIza...'} className="w-full h-10 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark font-mono text-sm outline-none focus:ring-2 focus:ring-brand-500/30" />
                    <input value={agentSettings.geminiModel} onChange={(event) => setAgentSettings({ ...agentSettings, geminiModel: event.target.value })} aria-label="Modelo Gemini" placeholder="gemini-3.6-flash" className="w-full h-9 mt-2 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm" />
                  </div>
                </div>
                <div className="flex items-start gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark"><Lock className="w-4 h-4 flex-shrink-0 text-emerald-600" /><span>As chaves são criptografadas no backend, vinculadas ao seu usuário e nunca retornam ao navegador. Somente o sufixo mascarado é exibido.</span></div>
              </section>

              <section className="space-y-4">
                <h4 className="text-sm font-semibold">Triagem automática e controle de consumo</h4>
                <label className="flex items-center justify-between gap-4 py-2 border-b border-line dark:border-line-dark"><span><span className="block text-sm font-medium">Analisar após a busca diária</span><span className="block text-xs text-ink-subtitle">Processa oportunidades novas depois da coleta PNCP.</span></span><input type="checkbox" checked={agentSettings.autoAnalyze} onChange={(event) => setAgentSettings({ ...agentSettings, autoAnalyze: event.target.checked })} className="w-4 h-4" /></label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="text-xs font-semibold">Nota mínima<input type="number" min="0" max="100" value={agentSettings.minimumScore} onChange={(event) => setAgentSettings({ ...agentSettings, minimumScore: Number(event.target.value) })} className="block w-full h-10 mt-1 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm" /></label>
                  <label className="text-xs font-semibold">Limite diário<input type="number" min="1" max="25" value={agentSettings.dailyLimit} onChange={(event) => setAgentSettings({ ...agentSettings, dailyLimit: Number(event.target.value) })} className="block w-full h-10 mt-1 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm" /></label>
                  <label className="text-xs font-semibold">Lembrete (minutos)<input type="number" min="0" value={agentSettings.calendarReminderMinutes} onChange={(event) => setAgentSettings({ ...agentSettings, calendarReminderMinutes: Number(event.target.value) })} className="block w-full h-10 mt-1 px-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm" /></label>
                </div>
                <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={agentSettings.calendarEnabled} onChange={(event) => setAgentSettings({ ...agentSettings, calendarEnabled: event.target.checked })} className="w-4 h-4" /> Gerar marcos para o Google Agenda</label>
                <label className="block text-xs font-semibold">Instruções permanentes do meu analista<textarea rows={5} value={agentSettings.analysisInstructions} onChange={(event) => setAgentSettings({ ...agentSettings, analysisInstructions: event.target.value })} placeholder="Ex.: priorizar locação, exigir margem mínima, alertar quando houver visita técnica..." className="block w-full mt-1 p-3 rounded-md border border-line dark:border-line-dark bg-bg dark:bg-bg-dark text-sm font-normal resize-y" /></label>
              </section>

              <div className="flex justify-end gap-2 pt-4 border-t border-line dark:border-line-dark">
                <button type="button" onClick={() => void handleTestAgent()} disabled={testingAgent || savingAgent} className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-line dark:border-line-dark text-sm font-medium disabled:opacity-50">{testingAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Testar conexão</button>
                <button type="button" onClick={() => void handleSaveAgent()} disabled={savingAgent} className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium disabled:opacity-50">{savingAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar meu analista</button>
              </div>
            </div>
          </div>
        )}

        {/* Conteúdo da Aba: EMPRESAS */}
        {activeTab === 'COMPANIES' && (
          <div className="animate-fade-in">
            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt/50 border-b border-line dark:border-line-dark flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                Cadastre os dados das empresas que participarão das licitações. Esses dados serão
                usados para preencher modelos automaticamente.
              </p>
              <button
                onClick={() => handleOpenCompanyModal()}
                className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium transition-colors shadow-sm whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                Nova Empresa
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {companies.map((company) => (
                <div
                  key={company.id}
                  className={`bg-surface dark:bg-surface-dark rounded-xl border p-5 transition-all relative group ${
                    company.isDefault
                      ? 'border-blue-500 shadow-md ring-1 ring-blue-500/20'
                      : 'border-line dark:border-line-dark shadow-sm hover:border-blue-300'
                  }`}
                >
                  {company.isDefault && (
                    <span className="absolute -top-2.5 right-4 bg-blue-500 dark:bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Padrão
                    </span>
                  )}

                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-lg bg-surface-alt dark:bg-surface-dark-alt border border-line dark:border-line-dark flex items-center justify-center text-ink-placeholder dark:text-ink-placeholder-dark overflow-hidden"
                        style={{ borderColor: company.primaryColor }}
                      >
                        {company.logo ? (
                          <img
                            src={company.logo}
                            alt="Logo"
                            className="w-full h-full object-contain p-1"
                          />
                        ) : (
                          <Briefcase className="w-5 h-5" />
                        )}
                      </div>
                      <div className="overflow-hidden">
                        <h3
                          className="font-semibold text-ink-title dark:text-ink-title-dark truncate"
                          title={company.corporateName}
                        >
                          {company.corporateName}
                        </h3>
                        <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{company.cnpj}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-ink-subtitle dark:text-ink-subtitle-dark mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                      <span className="truncate">
                        {company.city} - {company.state}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                      <span className="truncate">{company.representativeName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                      <span className="truncate">{company.email}</span>
                    </div>
                    {company.primaryColor && (
                      <div className="flex items-center gap-2 mt-2">
                        <Palette className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                        <div className="flex gap-1">
                          <div
                            className="w-4 h-4 rounded-full border border-line dark:border-line-dark"
                            style={{ backgroundColor: company.primaryColor }}
                            title="Cor Primária"
                          ></div>
                          <div
                            className="w-4 h-4 rounded-full border border-line dark:border-line-dark"
                            style={{ backgroundColor: company.secondaryColor || '#f1f5f9' }}
                            title="Cor Secundária"
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 pt-4 border-t border-line dark:border-line-dark">
                    <button
                      onClick={() => handleOpenCompanyModal(company)}
                      className="flex-1 py-1.5 text-xs font-medium text-ink-text dark:text-ink-text-dark bg-surface-alt dark:bg-surface-dark-alt hover:bg-surface-alt dark:bg-surface-dark-alt rounded-lg border border-line dark:border-line-dark transition-colors flex items-center justify-center gap-1"
                    >
                      <Edit2 className="w-3 h-3" /> Editar
                    </button>
                    <button
                      onClick={() => handleDeleteCompany(company.id, company.corporateName)}
                      className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ... (Abas USERS e AUDIT mantidas) ... */}
        {activeTab === 'USERS' && (
          <div className="animate-fade-in">
            {/* ... Conteúdo da aba de Usuários (sem alterações) ... */}
            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt/50 border-b border-line dark:border-line-dark flex flex-col md:flex-row justify-between items-center gap-4">
              {/* ... */}
              <div className="relative w-full md:w-96">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar usuários por nome, email..."
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
              </div>
              <button
                onClick={() => handleOpenUserModal()}
                className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium transition-colors shadow-sm whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4" />
                Novo Usuário
              </button>
            </div>
            {/* Tabela de Usuários */}
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-alt dark:bg-surface-dark-alt border-b border-line dark:border-line-dark">
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Permissão
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Departamento
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Status
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      App mobile
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider text-right">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              user.active
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300'
                                : 'bg-surface-alt dark:bg-surface-dark-alt text-ink-placeholder dark:text-ink-placeholder-dark'
                            }`}
                          >
                            {user.name.charAt(0).toUpperCase()}
                            {user.name.split(' ')[1]?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div
                              className={`font-medium text-sm ${
                                user.active ? 'text-ink-title dark:text-ink-title-dark' : 'text-ink-placeholder dark:text-ink-placeholder-dark'
                              }`}
                            >
                              {user.name}
                            </div>
                            <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">{getRoleBadge(user.role)}</td>
                      <td className="py-4 px-6 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{user.department || '-'}</td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => handleToggleActive(user.id, user.active)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                            user.active ? 'bg-green-500' : 'bg-surface-alt dark:bg-surface-dark-alt'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-surface dark:bg-surface-dark transition-transform ${
                              user.active ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-wrap gap-1.5">
                          <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium ${user.mobileAccessEnabled === false ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300' : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'}`}><Smartphone className="h-3.5 w-3.5" />{user.mobileAccessEnabled === false ? 'Bloqueado' : 'Liberado'}</span>
                          <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs font-medium ${user.mobileNotificationsEnabled === false ? 'border-line bg-surface-alt text-ink-subtitle dark:border-line-dark dark:bg-surface-dark-alt dark:text-ink-subtitle-dark' : 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300'}`}>{user.mobileNotificationsEnabled === false ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}{user.mobileNotificationsEnabled === false ? 'Sem push' : 'Push ativo'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleOpenUserModal(user)}
                            className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="p-1.5 text-ink-placeholder dark:text-ink-placeholder-dark hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ... (Aba AUDIT mantida) ... */}
        {activeTab === 'AUDIT' && (
          <div className="animate-fade-in">
            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt/50 border-b border-line dark:border-line-dark">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                </div>
                <input
                  type="text"
                  placeholder="Pesquisar nos logs (ação, detalhe, usuário)..."
                  value={logSearchTerm}
                  onChange={(e) => setLogSearchTerm(e.target.value)}
                  className="w-full bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-ink-text dark:text-ink-text-dark text-sm rounded-lg focus:ring-brand-500 focus:border-brand-500 block pl-10 p-2.5 outline-none"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-alt dark:bg-surface-dark-alt border-b border-line dark:border-line-dark">
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Data / Hora
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Ação
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Detalhes
                    </th>
                    <th className="py-3 px-6 text-xs font-semibold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider">
                      Usuário
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-surface-alt dark:bg-surface-dark-alt transition-colors">
                        <td className="py-3 px-6 align-middle">
                          <div className="bg-surface dark:bg-surface-dark p-1.5 rounded border border-line dark:border-line-dark w-fit shadow-sm">
                            {getLogIcon(log.type)}
                          </div>
                        </td>
                        <td className="py-3 px-6 align-middle text-sm text-ink-subtitle dark:text-ink-subtitle-dark whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                            {new Date(log.timestamp).toLocaleDateString('pt-BR')}{' '}
                            <span className="text-ink-placeholder dark:text-ink-placeholder-dark">|</span>{' '}
                            {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                          </div>
                        </td>
                        <td className="py-3 px-6 align-middle">
                          <span className="font-medium text-ink-title dark:text-ink-title-dark text-sm">{log.action}</span>
                        </td>
                        <td className="py-3 px-6 align-middle text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                          {log.details}
                        </td>
                        <td className="py-3 px-6 align-middle text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                            {log.user}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-ink-subtitle dark:text-ink-subtitle-dark">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <ShieldCheck className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark" />
                          <p>Nenhum registro encontrado.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'GOVERNANCE' && (
          <div className="animate-fade-in p-6 space-y-6">
            {governanceLoading && !governance ? (
              <div className="min-h-[240px] grid place-items-center text-ink-subtitle dark:text-ink-subtitle-dark"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : governance ? (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-ink-title dark:text-ink-title-dark">Governança e segurança</h3>
                    <p className="mt-1 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Políticas da organização, proteção de credenciais e evidências de backup.</p>
                  </div>
                  <button type="button" onClick={() => void handleSaveGovernance()} disabled={governanceSaving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                    {governanceSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar política
                  </button>
                </div>

                <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 border border-line dark:border-line-dark rounded-lg overflow-hidden divide-y sm:divide-y-0 sm:divide-x divide-line dark:divide-line-dark">
                  {[
                    { label: 'Usuários ativos', value: governance.indicators.activeUsers, icon: Users },
                    { label: 'Documentos preservados', value: governance.indicators.storedDocuments, icon: FileCheck2 },
                    { label: 'Chaves IA criptografadas', value: `${governance.indicators.encryptedAiKeys}/${governance.indicators.configuredAiKeys}`, icon: Lock },
                    { label: 'Isolamento por empresa', value: 'Ativo', icon: ShieldCheck },
                  ].map(({ label, value, icon: Icon }) => <div key={label} className="p-4 bg-surface dark:bg-surface-dark"><Icon className="w-4 h-4 text-brand-600" /><p className="mt-3 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{value}</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{label}</p></div>)}
                </section>

                <section className="rounded-lg border border-line dark:border-line-dark overflow-hidden">
                  <div className="flex flex-col gap-2 border-b border-line bg-surface-alt px-4 py-3 dark:border-line-dark dark:bg-surface-dark-alt sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Segurança de acesso</h4>
                      <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Sessões administrativas, segundo fator e tentativas recentes.</p>
                    </div>
                    {accessSecurityLoading ? <Loader2 className="h-4 w-4 animate-spin text-brand-600" /> : accessSecurity && <span className={accessSecurity.currentAccount.twoFactorEnabled ? 'text-xs font-medium text-emerald-700 dark:text-emerald-300' : 'text-xs font-medium text-amber-700 dark:text-amber-300'}>{accessSecurity.currentAccount.twoFactorEnabled ? '2FA ativo' : '2FA pendente'}</span>}
                  </div>
                  {accessSecurity ? (
                    <>
                      <div className="grid grid-cols-1 divide-y divide-line dark:divide-line-dark sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                        <div className="p-4"><Lock className="h-4 w-4 text-brand-600" /><p className="mt-2 text-xl font-semibold text-ink-title dark:text-ink-title-dark">{accessSecurity.currentAccount.activeSessions}</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Sessões ativas da conta</p></div>
                        <div className="p-4"><ShieldCheck className="h-4 w-4 text-brand-600" /><p className="mt-2 text-sm font-semibold text-ink-title dark:text-ink-title-dark">{accessSecurity.policy.sessionTtlSeconds / 3600}h</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Expiração máxima da sessão</p></div>
                        <div className="p-4"><AlertTriangle className="h-4 w-4 text-brand-600" /><p className="mt-2 text-sm font-semibold text-ink-title dark:text-ink-title-dark">{accessSecurity.policy.maxFailedAttempts} tentativas</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Antes do bloqueio progressivo</p></div>
                      </div>
                      <div className="border-t border-line p-4 dark:border-line-dark">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Recuperação da autenticação em duas etapas</p><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Gerar novos códigos invalida os anteriores. Cada código pode ser usado uma única vez.</p></div><button type="button" disabled={securityActionLoading || !accessSecurity.currentAccount.twoFactorEnabled} onClick={() => void handleRegenerateRecoveryCodes()} className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-brand-600 px-3 text-xs font-medium text-brand-700 dark:text-brand-300 disabled:opacity-50"><Key className="h-3.5 w-3.5" /> Gerar códigos</button></div>
                        {recoveryCodes.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">{recoveryCodes.map((code) => <code key={code} className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-center text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">{code}</code>)}</div>}
                      </div>
                      <div className="border-t border-line dark:border-line-dark">
                        <div className="px-4 py-3"><p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Sessões ativas</p></div>
                        <div className="divide-y divide-line dark:divide-line-dark">{accessSecurity.sessions.length ? accessSecurity.sessions.map((session) => <div key={session.id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"><div className="min-w-0"><p className="text-sm text-ink-title dark:text-ink-title-dark">{session.ipAddress || 'Origem não identificada'} {session.id === accessSecurity.currentSessionId ? <span className="ml-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">Sessão atual</span> : null}</p><p className="mt-1 truncate text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{session.userAgent || 'Navegador não informado'} · ativa em {new Date(session.lastSeenAt).toLocaleString('pt-BR')}</p></div>{session.id !== accessSecurity.currentSessionId && <button type="button" disabled={securityActionLoading} onClick={() => void handleRevokeSession(session.id)} className="h-8 shrink-0 rounded-md border border-rose-300 px-2.5 text-xs font-medium text-rose-700 dark:border-rose-800 dark:text-rose-300 disabled:opacity-50">Encerrar</button>}</div>) : <p className="px-4 py-4 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma sessão ativa encontrada.</p>}</div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm">
                          <thead className="border-y border-line bg-surface-alt text-xs text-ink-subtitle dark:border-line-dark dark:bg-surface-dark-alt dark:text-ink-subtitle-dark"><tr><th className="px-4 py-3 font-medium">Data</th><th className="px-4 py-3 font-medium">Administrador</th><th className="px-4 py-3 font-medium">Evento</th><th className="px-4 py-3 font-medium">Origem</th><th className="px-4 py-3 font-medium">Status</th></tr></thead>
                          <tbody className="divide-y divide-line dark:divide-line-dark">
                            {accessSecurity.events.length ? accessSecurity.events.map((event) => <tr key={event.id}><td className="whitespace-nowrap px-4 py-3 text-ink-subtitle dark:text-ink-subtitle-dark">{new Date(event.createdAt).toLocaleString('pt-BR')}</td><td className="px-4 py-3 text-ink-title dark:text-ink-title-dark">{event.user?.name || event.email}</td><td className="px-4 py-3 text-ink-subtitle dark:text-ink-subtitle-dark">{event.event.replaceAll('_', ' ')}</td><td className="px-4 py-3 text-ink-subtitle dark:text-ink-subtitle-dark">{event.ipAddress || '-'}</td><td className="px-4 py-3"><span className={event.success ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>{event.success ? 'Registrado' : 'Atenção'}</span></td></tr>) : <tr><td colSpan={5} className="px-4 py-8 text-center text-ink-subtitle dark:text-ink-subtitle-dark">Nenhum evento administrativo foi registrado ainda.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : !accessSecurityLoading && <p className="p-4 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Este histórico é exibido apenas para administradores.</p>}
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <div className="rounded-lg border border-line dark:border-line-dark p-4 bg-surface dark:bg-surface-dark">
                    <div className="flex items-start gap-3"><Clock className="w-5 h-5 mt-0.5 text-brand-600" /><div><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Política de retenção</h4><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">A retenção de anexos protege o acervo. Nenhum anexo é excluído automaticamente por esta tela.</p></div></div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <label>Agendamento do backup (cron)<input value={governance.policy.backupSchedule} onChange={(event) => setGovernance((current) => current ? { ...current, policy: { ...current.policy, backupSchedule: event.target.value } } : current)} className="mt-1 block h-9 w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-2" placeholder="30 2 * * *" /></label>
                      <label>Retenção de backups (dias)<input type="number" min="7" max="3650" value={governance.policy.backupRetentionDays} onChange={(event) => setGovernance((current) => current ? { ...current, policy: { ...current.policy, backupRetentionDays: Number(event.target.value) || 7 } } : current)} className="mt-1 block h-9 w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-2" /></label>
                      <label>Retenção de anexos (dias)<input type="number" min="30" max="36500" value={governance.policy.attachmentRetentionDays} onChange={(event) => setGovernance((current) => current ? { ...current, policy: { ...current.policy, attachmentRetentionDays: Number(event.target.value) || 30 } } : current)} className="mt-1 block h-9 w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-2" /></label>
                      <label>Retenção da auditoria (dias)<input type="number" min="30" max="36500" value={governance.policy.auditRetentionDays} onChange={(event) => setGovernance((current) => current ? { ...current, policy: { ...current.policy, auditRetentionDays: Number(event.target.value) || 30 } } : current)} className="mt-1 block h-9 w-full rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-2" /></label>
                    </div>
                  </div>
                  <div className="rounded-lg border border-line dark:border-line-dark p-4 bg-surface dark:bg-surface-dark">
                    <div className="flex items-start gap-3"><ShieldCheck className="w-5 h-5 mt-0.5 text-emerald-600" /><div><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Última evidência de backup</h4><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">O script da VPS valida os arquivos e o SHA-256 antes do registro.</p></div></div>
                    <dl className="mt-4 space-y-2 text-sm"><div className="flex justify-between gap-4"><dt className="text-ink-subtitle dark:text-ink-subtitle-dark">Status</dt><dd className="font-medium text-ink-title dark:text-ink-title-dark">{governance.policy.backupLastStatus || 'Sem registro'}</dd></div><div className="flex justify-between gap-4"><dt className="text-ink-subtitle dark:text-ink-subtitle-dark">Executado</dt><dd className="font-medium text-ink-title dark:text-ink-title-dark">{governance.policy.backupLastCompletedAt ? new Date(governance.policy.backupLastCompletedAt).toLocaleString('pt-BR') : '-'}</dd></div><div className="flex justify-between gap-4"><dt className="text-ink-subtitle dark:text-ink-subtitle-dark">Verificado</dt><dd className="font-medium text-ink-title dark:text-ink-title-dark">{governance.policy.backupLastVerifiedAt ? new Date(governance.policy.backupLastVerifiedAt).toLocaleString('pt-BR') : '-'}</dd></div><div><dt className="text-ink-subtitle dark:text-ink-subtitle-dark">Arquivo</dt><dd className="mt-1 break-all font-medium text-ink-title dark:text-ink-title-dark">{governance.policy.backupLastFileName || '-'}</dd></div></dl>
                  </div>
                </section>

                <section className="rounded-lg border border-line dark:border-line-dark overflow-hidden"><div className="px-4 py-3 bg-surface-alt dark:bg-surface-dark-alt border-b border-line dark:border-line-dark"><h4 className="font-semibold text-ink-title dark:text-ink-title-dark">Alterações auditadas de segurança</h4></div><div className="divide-y divide-line dark:divide-line-dark">{governance.recentAudit.length ? governance.recentAudit.slice(0, 8).map((entry) => <div key={entry.id} className="px-4 py-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">{entry.action}</p><p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{entry.userName}</p></div><time className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{new Date(entry.timestamp).toLocaleString('pt-BR')}</time></div>) : <p className="p-4 text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Nenhuma alteração registrada.</p>}</div></section>
              </>
            ) : <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">Não foi possível carregar os dados de governança.</p>}
          </div>
        )}
      </div>

      {/* Modal de Usuário (Mantido igual) */}
      {isUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          {/* ... Conteúdo do modal de usuário ... */}
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                {editingUser ? 'Editar Usuário' : 'Adicionar Novo Usuário'}
              </h3>
              <button
                onClick={() => setIsUserModalOpen(false)}
                className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label
                    htmlFor="user-name"
                    className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1"
                  >
                    Nome Completo *
                  </label>
                  <input
                    id="user-name"
                    type="text"
                    className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                    placeholder="Ex: João da Silva"
                    value={userForm.name}
                    onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <label
                    htmlFor="user-email"
                    className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1"
                  >
                    E-mail *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                    <input
                      type="email"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      placeholder="usuario@empresa.com"
                      id="user-email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    />
                  </div>
                </div>
                {/* ... Resto do formulário de usuário ... */}
                <div>
                  <label
                    htmlFor="user-department"
                    className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1"
                  >
                    Departamento
                  </label>
                  <input
                    id="user-department"
                    type="text"
                    className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                    placeholder="Ex: Jurídico"
                    value={userForm.department}
                    onChange={(e) => setUserForm({ ...userForm, department: e.target.value })}
                  />
                </div>
                <div>
                  <label
                    htmlFor="user-status"
                    className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1"
                  >
                    Status
                  </label>
                  <select
                    id="user-status"
                    value={userForm.active ? 'true' : 'false'}
                    onChange={(e) =>
                      setUserForm({ ...userForm, active: e.target.value === 'true' })
                    }
                    className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none bg-surface dark:bg-surface-dark"
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </select>
                </div>
              </div>

              <fieldset className="pt-2">
                <legend className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-2">
                  Nível de Permissão
                </legend>
                <div className="grid grid-cols-1 gap-2">
                  {(['ADMIN', 'MANAGER', 'OPERATOR', 'VIEWER'] as UserRole[]).map((role) => (
                    <label
                      key={role}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        userForm.role === role
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/60 shadow-sm'
                          : 'bg-surface dark:bg-surface-dark border-line dark:border-line-dark hover:border-line-strong dark:border-line-dark-strong'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        className="mt-1"
                        checked={userForm.role === role}
                        onChange={() => setUserForm({ ...userForm, role })}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-ink-title dark:text-ink-title-dark">
                            {role === 'ADMIN'
                              ? 'Administrador'
                              : role === 'MANAGER'
                                ? 'Gestor'
                                : role === 'OPERATOR'
                                  ? 'Operador'
                                  : 'Visualizador'}
                          </span>
                          {getRoleBadge(role)}
                        </div>
                        <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">{getRoleDescription(role)}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="pt-2">
                <div className="mb-3">
                  <legend className="block text-xs font-medium text-ink-text dark:text-ink-text-dark">Aplicativo mobile</legend>
                  <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Defina se este usuário pode utilizar o app e receber alertas push no aparelho cadastrado.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${userForm.mobileAccessEnabled === false ? 'border-rose-300 bg-rose-50/70 dark:border-rose-800 dark:bg-rose-950/20' : 'border-line bg-surface dark:border-line-dark dark:bg-surface-dark'}`}>
                    <input type="checkbox" checked={userForm.mobileAccessEnabled !== false} onChange={(event) => setUserForm({ ...userForm, mobileAccessEnabled: event.target.checked, mobileNotificationsEnabled: event.target.checked ? userForm.mobileNotificationsEnabled : false })} className="mt-0.5 rounded border-line-strong text-brand-500 focus:ring-brand-500" />
                    <span><span className="flex items-center gap-1.5 text-sm font-semibold text-ink-title dark:text-ink-title-dark"><Smartphone className="h-4 w-4" />Liberar acesso mobile</span><span className="mt-1 block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Bloqueia a utilização do aplicativo neste usuário.</span></span>
                  </label>
                  <label className={`flex items-start gap-3 rounded-lg border p-3 ${userForm.mobileAccessEnabled === false ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} border-line bg-surface dark:border-line-dark dark:bg-surface-dark`}>
                    <input type="checkbox" checked={userForm.mobileAccessEnabled !== false && userForm.mobileNotificationsEnabled !== false} disabled={userForm.mobileAccessEnabled === false} onChange={(event) => setUserForm({ ...userForm, mobileNotificationsEnabled: event.target.checked })} className="mt-0.5 rounded border-line-strong text-brand-500 focus:ring-brand-500" />
                    <span><span className="flex items-center gap-1.5 text-sm font-semibold text-ink-title dark:text-ink-title-dark"><Bell className="h-4 w-4" />Notificações push</span><span className="mt-1 block text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Permite alertas de prazos e monitoramento no aparelho.</span></span>
                  </label>
                </div>
              </fieldset>

              <fieldset className="pt-2">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <legend className="block text-xs font-medium text-ink-text dark:text-ink-text-dark">
                      Menus e submenus liberados
                    </legend>
                    <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                      A seleção restringe a navegação e o acesso aos módulos da API deste usuário.
                    </p>
                  </div>
                  {userForm.role === 'ADMIN' && (
                    <span className="shrink-0 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded">
                      Acesso total
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {MENU_PERMISSION_GROUPS.map((group) => {
                    const groupKeys = group.items.map((item) => item.key);
                    const allSelected = groupKeys.every((key) => selectedMenuKeys.has(key));
                    return (
                      <section key={group.label} className="border border-line dark:border-line-dark rounded-lg p-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-ink-title dark:text-ink-title-dark cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userForm.role === 'ADMIN' || allSelected}
                            disabled={userForm.role === 'ADMIN'}
                            onChange={() => toggleMenuGroup(groupKeys)}
                            className="rounded border-line-strong text-brand-500 focus:ring-brand-500"
                          />
                          {group.label}
                        </label>
                        <div className="mt-2 space-y-1.5 pl-1">
                          {group.items.map((item) => (
                            <label key={item.key} className="flex items-center gap-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark cursor-pointer">
                              <input
                                type="checkbox"
                                checked={userForm.role === 'ADMIN' || selectedMenuKeys.has(item.key)}
                                disabled={userForm.role === 'ADMIN'}
                                onChange={() => toggleMenuKey(item.key)}
                                className="rounded border-line-strong text-brand-500 focus:ring-brand-500"
                              />
                              {item.label}
                            </label>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </fieldset>
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
              <button
                onClick={() => setIsUserModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveUser}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
              >
                <Save className="w-4 h-4" />
                Salvar Usuário
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Empresa (Criar/Editar) */}
      {isCompanyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-surface dark:bg-surface-dark rounded-xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-line dark:border-line-dark flex justify-between items-center bg-surface-alt dark:bg-surface-dark-alt">
              <h3 className="font-semibold text-ink-title dark:text-ink-title-dark flex items-center gap-2">
                <Building className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                {editingCompany ? 'Editar Empresa' : 'Cadastrar Nova Empresa'}
              </h3>
              <button
                onClick={() => setIsCompanyModalOpen(false)}
                className="text-ink-placeholder dark:text-ink-placeholder-dark hover:text-ink-subtitle dark:text-ink-subtitle-dark"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              {/* Logotipo Upload */}
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-line-strong dark:border-line-dark-strong bg-surface-alt dark:bg-surface-dark-alt flex items-center justify-center overflow-hidden"
                  style={{ borderColor: companyForm.primaryColor }}
                >
                  {companyForm.logo ? (
                    <img
                      src={companyForm.logo}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  )}
                </div>
                <div>
                  <label className="cursor-pointer bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong hover:bg-surface-alt dark:bg-surface-dark-alt text-ink-text dark:text-ink-text-dark px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm flex items-center gap-2 transition-colors">
                    <Upload className="w-3 h-3" />
                    Carregar Logotipo
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </label>
                  <p className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark mt-1">
                    Recomendado: PNG Transparente (Max 500KB)
                  </p>
                </div>
              </div>

              {/* Personalização Visual (Timbrado) */}
              <div className="bg-surface-alt dark:bg-surface-dark-alt p-4 rounded-lg border border-line dark:border-line-dark">
                <h4 className="text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider mb-3 flex items-center gap-1">
                  <Palette className="w-3.5 h-3.5" />
                  Personalização do Timbrado
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Cor Primária (Cabeçalho)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={companyForm.primaryColor || '#2563eb'}
                        onChange={(e) =>
                          setCompanyForm({ ...companyForm, primaryColor: e.target.value })
                        }
                        className="h-8 w-12 cursor-pointer rounded border border-line-strong dark:border-line-dark-strong p-0"
                      />
                      <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-mono">
                        {companyForm.primaryColor}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Cor Secundária (Fundo/Rodapé)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={companyForm.secondaryColor || '#f1f5f9'}
                        onChange={(e) =>
                          setCompanyForm({ ...companyForm, secondaryColor: e.target.value })
                        }
                        className="h-8 w-12 cursor-pointer rounded border border-line-strong dark:border-line-dark-strong p-0"
                      />
                      <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark font-mono">
                        {companyForm.secondaryColor}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dados Básicos */}
              <div>
                <h4 className="text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider mb-3">
                  Dados Corporativos
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Razão Social *
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.corporateName}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, corporateName: e.target.value })
                      }
                      placeholder="Razão Social Ltda"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Nome Fantasia
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.tradeName}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, tradeName: e.target.value })
                      }
                      placeholder="Nome Fantasia"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">CNPJ *</label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.cnpj}
                      onChange={(e) => setCompanyForm({ ...companyForm, cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Website</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-2.5 w-3.5 h-3.5 text-ink-placeholder dark:text-ink-placeholder-dark" />
                      <input
                        type="text"
                        className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                        value={companyForm.website}
                        onChange={(e) =>
                          setCompanyForm({ ...companyForm, website: e.target.value })
                        }
                        placeholder="www.seusite.com.br"
                      />
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      E-mail Corporativo
                    </label>
                    <input
                      type="email"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.email}
                      onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                      placeholder="contato@empresa.com"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Telefone</label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.phone}
                      onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="pt-2 border-t border-line dark:border-line-dark">
                <h4 className="text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider mb-3">
                  Endereço
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Logradouro Completo
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.address}
                      onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                      placeholder="Rua, Número, Bairro"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">Cidade</label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.city}
                      onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Estado (UF)
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.state}
                      onChange={(e) => setCompanyForm({ ...companyForm, state: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">CEP</label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.zipCode}
                      onChange={(e) => setCompanyForm({ ...companyForm, zipCode: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Representante */}
              <div className="pt-2 border-t border-line dark:border-line-dark">
                <h4 className="text-xs font-bold text-ink-subtitle dark:text-ink-subtitle-dark uppercase tracking-wider mb-3">
                  Representante Legal
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Nome Completo
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.representativeName}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, representativeName: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">CPF</label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.representativeCpf}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, representativeCpf: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ink-text dark:text-ink-text-dark mb-1">
                      Cargo / Função
                    </label>
                    <input
                      type="text"
                      className="w-full border border-line-strong dark:border-line-dark-strong rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 outline-none"
                      value={companyForm.representativeRole}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, representativeRole: e.target.value })
                      }
                      placeholder="Sócio, Procurador..."
                    />
                  </div>
                </div>
              </div>

              {/* Checkbox Padrão */}
              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={companyForm.isDefault}
                    onChange={(e) =>
                      setCompanyForm({ ...companyForm, isDefault: e.target.checked })
                    }
                    className="rounded text-blue-600 dark:text-blue-300 focus:ring-brand-500 h-4 w-4 border-line-strong dark:border-line-dark-strong"
                  />
                  <span className="text-sm text-ink-text dark:text-ink-text-dark font-medium">
                    Definir como empresa padrão para documentos
                  </span>
                </label>
              </div>
            </div>

            <div className="p-4 bg-surface-alt dark:bg-surface-dark-alt border-t border-line dark:border-line-dark flex justify-end gap-3">
              <button
                onClick={() => setIsCompanyModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-ink-subtitle dark:text-ink-subtitle-dark bg-surface dark:bg-surface-dark border border-line-strong dark:border-line-dark-strong rounded-lg hover:bg-surface-alt dark:bg-surface-dark-alt"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveCompany}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 flex items-center gap-2 shadow-sm"
              >
                <Save className="w-4 h-4" /> Salvar Empresa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
